import React, { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { supabase } from '../../../lib/supabaseClient';
import CreateGroupDialog from './CreateGroupDialog';
import { useNavigate } from 'react-router-dom';

interface Group {
  id: string;
  name: string;
  is_public: boolean;
  created_at: string;
}

const GroupsPage: React.FC = () => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      await fetchGroups();
    })();

    // Subscribe to relevant realtime changes so the list updates automatically.
    // We listen to changes on:
    // 1. `groups`   – group details (name changes, etc.)
    // 2. `group_members` – when the current user gets added / removed
    // 3. `invites`  – pending email invites for the current user
    const setupRealtime = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const channel = supabase.channel('groups-page');

      // Any change to the groups table
      channel.on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'groups' },
        () => fetchGroups()
      );

      // Membership changes for the current user
      channel.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'group_members',
          filter: `user_id=eq.${user.id}`,
        },
        () => fetchGroups()
      );

      // Invite changes for the current user (matched via email)
      channel.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'invites',
          filter: `email=eq.${user.email}`,
        },
        () => fetchGroups()
      );

      const subscription = channel.subscribe();

      return () => {
        supabase.removeChannel(subscription);
      };
    };

    const cleanupPromise = setupRealtime();

    // Cleanup on unmount
    return () => {
      cleanupPromise.then(cleanup => {
        if (typeof cleanup === 'function') cleanup();
      });
    };
  }, []);

  const fetchGroups = async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return;

    // 1. Groups where the user is already a member (any status other than "rejected")
    const { data: memberships } = await supabase
      .from('group_members')
      .select('group_id')
      .eq('user_id', user.id);

    // 2. Groups the user has been invited to (matched via email)
    const { data: emailInvites } = await supabase
      .from('invites')
      .select('group_id')
      .eq('email', user.email);

    // Combine & dedupe ids
    const groupIds = Array.from(
      new Set([
        ...(memberships ?? []).map((m: any) => m.group_id),
        ...(emailInvites ?? []).map((i: any) => i.group_id),
      ])
    );

    if (groupIds.length === 0) {
      setGroups([]);
      return;
    }

    const { data, error } = await supabase.from('groups').select('*').in('id', groupIds);
    if (!error) setGroups(data as Group[]);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">My Groups</h1>
        <button
          className="px-3 py-2 rounded-md bg-blue-600 text-white flex items-center gap-1 text-sm"
          onClick={() => setShowCreate(true)}
        >
          <Plus size={16} /> Create Group
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {groups.map(g => (
          <div
            key={g.id}
            className="bg-white dark:bg-gray-800 shadow-sm rounded border p-4 cursor-pointer hover:shadow-md transition"
            onClick={() => navigate(`/groups/${g.id}`)}
          >
            <h3 className="font-medium text-lg mb-1 flex items-center">
              {g.name}
              {g.is_public && <span className="text-xs ml-2 px-2 py-0.5 bg-gray-200 dark:bg-gray-700 rounded">public</span>}
            </h3>
            <p className="text-xs text-gray-500">Created {new Date(g.created_at).toLocaleDateString()}</p>
          </div>
        ))}
      </div>
      {showCreate && (
        <CreateGroupDialog
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            fetchGroups();
            setShowCreate(false);
          }}
        />
      )}
    </div>
  );
};

export default GroupsPage; 